export class ContractInputError extends Error {}

export type ContractParty = {
  name: string;
  id_number: string;
  tax_id: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  postal_code: string;
};

export type ContractInstallment = {
  label: string;
  amount: string;
  due: string;
};

export type ContractBank = {
  bank: string;
  iban: string;
  holder: string;
};

export type ContractDraft = {
  signed_place: string;
  signed_on: string;
  company_name: string;
  company_seat: string;
  company_address: string;
  company_afm: string;
  company_doy: string;
  venue_name: string;
  representative: string;
  parties: ContractParty[];
  event_kind: string;
  event_date_label: string;
  start_time: string;
  end_time: string;
  ceremony: string;
  guests: string;
  minimum_guests: string;
  service: string;
  venue_place: string;
  menu: string;
  price_per_person: string;
  vat_note: string;
  child_rule: string;
  dj_company: string;
  dj_client_sound: string;
  lights: string;
  open_bar: string;
  installments: ContractInstallment[];
  banks: ContractBank[];
};

export type ContractStatus = {
  state: "draft" | "sent" | "signed";
  sent_at: string | null;
  signed_at: string | null;
  signer_name: string | null;
};

export type ContractSource = {
  parties: { given_name: string; family_name: string; phone: string | null; email: string | null }[];
  contractDate: string | null;
  depositDate: string | null;
  eventDate: string | null;
  eventTypeLabel: string | null;
  venueName: string | null;
  guests: number | null;
  serviceLabel: string | null;
  menuLabel: string | null;
  ceremony: string | null;
  today: string;
};

const INSTALLMENT_LABELS = [
  "Προκαταβολή, 1η δόση (20%)",
  "2η δόση (20%)",
  "3η δόση (40%)",
  "Εξόφληση υπολοίπου (20%)",
] as const;

const INSTALLMENT_SHARES = [0.2, 0.2, 0.4, 0.2] as const;

export function emptyParty(): ContractParty {
  return { name: "", id_number: "", tax_id: "", phone: "", email: "", street: "", city: "", postal_code: "" };
}

export function greekLongDate(iso: string | null | undefined): string {
  const text = (iso ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return "";
  const date = new Date(`${text}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  const formatted = new Intl.DateTimeFormat("el-GR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Athens",
  }).format(date);
  return formatted.charAt(0).toLocaleUpperCase("el") + formatted.slice(1);
}

export function parseMoney(value: string | null | undefined): number | null {
  let text = (value ?? "").trim().replaceAll("€", "").replaceAll(" ", "");
  if (!text) return null;
  if (text.includes(",") && text.includes(".")) text = text.replaceAll(".", "").replace(",", ".");
  else if (text.includes(",")) text = text.replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const amount = Number(text);
  if (!Number.isFinite(amount) || amount > 10_000_000) return null;
  return Math.round(amount * 100) / 100;
}

export function formatMoney(amount: number): string {
  return `${new Intl.NumberFormat("el-GR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} €`;
}

export function contractTotal(draft: Pick<ContractDraft, "price_per_person" | "guests">): number | null {
  const price = parseMoney(draft.price_per_person);
  const guests = Number(draft.guests.trim());
  if (price == null || !Number.isInteger(guests) || guests < 1 || guests > 100000) return null;
  return Math.round(price * guests * 100) / 100;
}

export function suggestedInstallments(total: number | null): string[] {
  if (total == null) return ["", "", "", ""];
  const cents = Math.round(total * 100);
  const amounts = INSTALLMENT_SHARES.slice(0, 3).map((share) => Math.round(cents * share));
  amounts.push(cents - amounts.reduce((sum, item) => sum + item, 0));
  return amounts.map((item) => formatMoney(item / 100));
}

export function prefillContract(source: ContractSource): ContractDraft {
  const named = source.parties
    .map((party) => ({
      ...emptyParty(),
      name: `${party.given_name} ${party.family_name}`.trim(),
      phone: party.phone ?? "",
      email: party.email ?? "",
    }))
    .filter((party) => party.name || party.email || party.phone)
    .slice(0, 4);
  const guests = source.guests != null ? String(source.guests) : "";
  const venue = source.venueName?.trim() || "ΚΤΗΜΑ 28";
  const total = contractTotal({ price_per_person: "", guests });
  const amounts = suggestedInstallments(total);
  return {
    signed_place: "Αφίδνες Αττικής",
    signed_on: source.contractDate || source.today,
    company_name: "ΝΑΓΙΑ Ι.Κ.Ε.",
    company_seat: "Αφίδνες Αττικής",
    company_address: "28ο χλμ. Αθηνών - Λαμίας",
    company_afm: "800817208",
    company_doy: "Κηφισιάς",
    venue_name: venue,
    representative: "Αθανασία Καππή",
    parties: named.length ? named : [emptyParty()],
    event_kind: source.eventTypeLabel?.trim() || "",
    event_date_label: greekLongDate(source.eventDate),
    start_time: "",
    end_time: "",
    ceremony: source.ceremony?.trim() || "",
    guests,
    minimum_guests: guests,
    service: source.serviceLabel?.trim() || "",
    venue_place: venue,
    menu: source.menuLabel?.trim() || "",
    price_per_person: "",
    vat_note: "πλέον Φ.Π.Α. (13% και 24%, σύμφωνα με τις εκάστοτε ισχύουσες διατάξεις)",
    child_rule: "Για παιδιά ηλικίας 3 έως 11 ετών, πέραν των ενηλίκων, ισχύει χρέωση 50% της τιμής ενηλίκου, ήτοι ανά δύο (2) παιδιά υπολογίζεται ένα (1) χρεώσιμο άτομο.",
    dj_company: "",
    dj_client_sound: "",
    lights: "",
    open_bar: "",
    installments: INSTALLMENT_LABELS.map((label, index) => ({
      label,
      amount: amounts[index] ?? "",
      due: index === 0 ? greekLongDate(source.depositDate) : "",
    })),
    banks: [
      { bank: "Εθνική Τράπεζα", iban: "GR34 0110 2710 0000 2710 0195 778", holder: "ΝΑΓΙΑ Ι.Κ.Ε." },
      { bank: "Alpha Bank", iban: "GR79 0140 2840 2840 0200 2006 796", holder: "ΝΑΓΙΑ Ι.Κ.Ε." },
    ],
  };
}

export function applyPrice(previous: ContractDraft, next: ContractDraft): ContractDraft {
  const before = suggestedInstallments(contractTotal(previous));
  const after = suggestedInstallments(contractTotal(next));
  return {
    ...next,
    installments: next.installments.map((row, index) => {
      const current = row.amount.trim();
      if (current && current !== before[index]) return row;
      return { ...row, amount: after[index] ?? "" };
    }),
  };
}

export function contractMessage(venue: string, link: string): { subject: string; text: string; html: string } {
  const house = venue.trim() || "τον χώρο";
  const subject = `Ιδιωτικό συμφωνητικό — ${house}`;
  const text = [
    "Καλησπέρα,",
    "",
    `Σας στέλνουμε το ιδιωτικό συμφωνητικό για την εκδήλωσή σας στο ${house}.`,
    "Ανοίξτε τον σύνδεσμο, διαβάστε το και βάλτε την υπογραφή σας:",
    link,
    "",
    "Με εκτίμηση,",
    house,
  ].join("\n");
  const safe = escapeHtml(link);
  const html = [
    "<p>Καλησπέρα,</p>",
    `<p>Σας στέλνουμε το ιδιωτικό συμφωνητικό για την εκδήλωσή σας στο ${escapeHtml(house)}.</p>`,
    `<p><a href="${safe}">Άνοιγμα συμφωνητικού και υπογραφή</a></p>`,
    `<p>Με εκτίμηση,<br>${escapeHtml(house)}</p>`,
  ].join("");
  return { subject, text, html };
}

export function contractIntro(draft: ContractDraft): string {
  const when = greekLongDate(draft.signed_on) || draft.signed_on.trim() || "………………";
  const names = draft.parties.map((party) => party.name.trim()).filter(Boolean);
  const clients = names.length ? names.join(" και ") : "………………";
  const venue = draft.venue_name.trim() || "………………";
  return `Στις ${draft.signed_place.trim() || "………………"}, σήμερα την ${when}, μεταξύ της εταιρείας «${draft.company_name.trim() || "………………"}» (${draft.company_seat.trim()}, ${draft.company_address.trim()}, ΑΦΜ ${draft.company_afm.trim() || "………………"}, ΔΟΥ ${draft.company_doy.trim() || "………………"}), που λειτουργεί τον χώρο «${venue}» και εκπροσωπείται από ${draft.representative.trim() || "………………"}, και των ${clients}.`;
}

export function contractArticles(draft: ContractDraft): { heading: string; lines: string[] }[] {
  const venue = draft.venue_name.trim() || "τον χώρο";
  const total = contractTotal(draft);
  const price = parseMoney(draft.price_per_person);
  const guests = draft.guests.trim() || "………………";
  const minimum = draft.minimum_guests.trim() || guests;
  const priceLine = price != null && total != null
    ? `Το συνολικό τίμημα ορίζεται σε ${formatMoney(price)} ανά άτομο για ${guests} άτομα, ήτοι συνολικά ${formatMoney(total)}, ${draft.vat_note.trim() || "πλέον Φ.Π.Α."}.`
    : "Το τίμημα ανά άτομο και το σύνολο συμπληρώνονται πριν την αποστολή.";
  const money = (value: string) => value.trim() || "………………";
  return [
    {
      heading: "Άρθρο 1 — Αντικείμενο",
      lines: [
        `Η εταιρεία αναλαμβάνει να οργανώσει την εκδήλωση στον χώρο «${venue}».`,
        `Είδος: ${draft.event_kind.trim() || "………………"}. Ημερομηνία: ${draft.event_date_label.trim() || "………………"}.`,
        `Ώρα: ${[draft.start_time, draft.end_time].filter(Boolean).join(" / ") || "………………"}${draft.ceremony.trim() ? `. ${draft.ceremony.trim()}` : ""}.`,
        `Άτομα: ${guests}. Ελάχιστη χρεώσιμη ποσότητα: ${minimum}. Σερβίρισμα: ${draft.service.trim() || "………………"}.`,
        `Χώρος: ${draft.venue_place.trim() || venue}. Μενού: ${draft.menu.trim() || "………………"}.`,
        "Το επιλεγμένο μενού αποτελεί αναπόσπαστο μέρος του συμφωνητικού.",
      ],
    },
    {
      heading: "Άρθρο 2 — Τίμημα",
      lines: [
        priceLine,
        `Ελάχιστη χρεώσιμη ποσότητα: ${minimum} άτομα.`,
        draft.child_rule.trim(),
      ].filter(Boolean),
    },
    {
      heading: "Άρθρο 3 — Περιλαμβανόμενες υπηρεσίες",
      lines: [
        "Στο τίμημα περιλαμβάνονται το μενού και το μπαρ του επιλεγμένου πακέτου, ο εξοπλισμός, το service, οι παρκαδόροι, ο καθαρισμός των χώρων υγιεινής κατά την εκδήλωση και η ενοικίαση του χώρου.",
      ],
    },
    {
      heading: "Άρθρο 4 — Πρόσθετες χρεώσεις",
      lines: [
        `DJ της εταιρείας: ${money(draft.dj_company)}. Περιλαμβάνει την αμοιβή και τη χρήση των ηχητικών, εφόσον κλειστεί και εξοφληθεί έξι μήνες πριν την εκδήλωση.`,
        `DJ του πελάτη, χρήση ηχητικής εγκατάστασης: ${money(draft.dj_client_sound)}.`,
        `Φωτορυθμικά πίστας: ${money(draft.lights)}.`,
        `Open bar: ${money(draft.open_bar)}.`,
      ],
    },
    {
      heading: "Άρθρο 5 — DJ του πελάτη",
      lines: [
        `${venue} δέχεται DJ του πελάτη μόνο αν απασχολείται και ασφαλίζεται νόμιμα, με πίνακα προσωπικού (Ε4) και αστυνομική ταυτότητα.`,
        "Η γραφειοκρατία της πρόσληψης βαρύνει τον πελάτη. Δεν γίνεται δεκτός DJ με μπλοκάκι ή εργόσημο.",
        "Ο DJ χρησιμοποιεί μόνο τον ηχητικό εξοπλισμό της εταιρείας. Δικό του ηχητικό σύστημα δεν επιτρέπεται.",
      ],
    },
    {
      heading: "Άρθρο 6 — Εξόφληση",
      lines: [
        ...draft.installments.map((row) => `${row.label}: ${row.amount.trim() || "………………"}${row.due.trim() ? `, έως ${row.due.trim()}` : ""}.`),
        "Η οριστική κράτηση γίνεται με την προκαταβολή. Η προκαταβολή δεν επιστρέφεται.",
        ...draft.banks.filter((bank) => bank.iban.trim()).map((bank) => `${bank.bank.trim() || "Τράπεζα"} — ${bank.iban.trim()} — ${bank.holder.trim() || draft.company_name}`),
      ],
    },
    {
      heading: "Άρθρο 7 — Αριθμός ατόμων",
      lines: [
        "Ο τελικός αριθμός επιβεβαιώνεται επτά ημέρες πριν. Αύξηση γίνεται έως 24 ώρες πριν, με λίστα καλεσμένων.",
        "Αν προσέλθουν λιγότεροι, η χρέωση γίνεται βάσει των επιβεβαιωθέντων.",
      ],
    },
    {
      heading: "Άρθρο 8 — Λειτουργία χώρου",
      lines: [
        "Ζωντανή μουσική γίνεται δεκτή μόνο με νόμιμη απασχόληση και ασφάλιση, πίνακα προσωπικού και ταυτότητα.",
        "Απαγορεύονται βεγγαλικά, σπασίματα και sparkles. Επιτρέπονται μόνο σιντριβάνια μέσω συνεργάτη της εταιρείας.",
      ],
    },
    {
      heading: "Άρθρο 9 — Ακύρωση και αναβολή",
      lines: [
        `Ακύρωση που γνωστοποιείται πάνω από 30 ημέρες πριν οδηγεί σε νέα ημερομηνία, εφόσον υπάρχει διαθεσιμότητα στο ${venue}.`,
        "Μέχρι την 30ή Απριλίου 2028 η εκδήλωση γίνεται με το ίδιο τίμημα και η προκαταβολή συμψηφίζεται. Από την 1η Μαΐου 2028 ισχύει η χρέωση της νέας σεζόν.",
        "Η προκαταβολή δεν επιστρέφεται, ούτε σε ανωτέρα βία.",
      ],
    },
    {
      heading: "Άρθρο 10 — Γενικές διατάξεις",
      lines: [
        "Οι πελάτες ευθύνονται εις ολόκληρον. Το συμφωνητικό και το μενού αντικαθιστούν κάθε προηγούμενη συνεννόηση.",
        "Ισχύει το ελληνικό δίκαιο. Αρμόδια είναι τα δικαστήρια των Αθηνών.",
        "Οι πελάτες δηλώνουν ότι διάβασαν και αποδέχονται τους όρους. Η υπογραφή μπαίνει από τον σύνδεσμο του mail.",
      ],
    },
  ];
}

export function parseContract(value: unknown): ContractDraft {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ContractInputError("Το συμφωνητικό δεν είναι έγκυρο.");
  const record = value as Record<string, unknown>;
  const parties = Array.isArray(record.parties) ? record.parties.slice(0, 4).map(parseParty) : [];
  const installments = Array.isArray(record.installments) ? record.installments.slice(0, 4).map(parseInstallment) : [];
  const banks = Array.isArray(record.banks) ? record.banks.slice(0, 3).map(parseBank) : [];
  if (!parties.length) throw new ContractInputError("Χρειάζεται τουλάχιστον ένας πελάτης.");
  while (installments.length < 4) installments.push({ label: INSTALLMENT_LABELS[installments.length] ?? "Δόση", amount: "", due: "" });
  return {
    signed_place: clip(record.signed_place, 120),
    signed_on: dateOrBlank(record.signed_on),
    company_name: clip(record.company_name, 120),
    company_seat: clip(record.company_seat, 120),
    company_address: clip(record.company_address, 160),
    company_afm: clip(record.company_afm, 20),
    company_doy: clip(record.company_doy, 80),
    venue_name: clip(record.venue_name, 120),
    representative: clip(record.representative, 120),
    parties,
    event_kind: clip(record.event_kind, 80),
    event_date_label: clip(record.event_date_label, 120),
    start_time: clip(record.start_time, 40),
    end_time: clip(record.end_time, 40),
    ceremony: clip(record.ceremony, 240),
    guests: clip(record.guests, 20),
    minimum_guests: clip(record.minimum_guests, 20),
    service: clip(record.service, 80),
    venue_place: clip(record.venue_place, 180),
    menu: clip(record.menu, 120),
    price_per_person: clip(record.price_per_person, 40),
    vat_note: clip(record.vat_note, 240),
    child_rule: clip(record.child_rule, 400),
    dj_company: clip(record.dj_company, 40),
    dj_client_sound: clip(record.dj_client_sound, 40),
    lights: clip(record.lights, 40),
    open_bar: clip(record.open_bar, 40),
    installments,
    banks: banks.length ? banks : prefillContract(blankSource()).banks,
  };
}

export function signatureData(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("data:image/png;base64,")) {
    throw new ContractInputError("Η υπογραφή δεν διαβάστηκε. Ζωγράφισέ την ξανά.");
  }
  if (value.length > 120_000) throw new ContractInputError("Η υπογραφή είναι πολύ μεγάλη. Ζωγράφισέ την ξανά.");
  return value;
}

function parseParty(value: unknown): ContractParty {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ContractInputError("Τα στοιχεία πελάτη δεν είναι έγκυρα.");
  const record = value as Record<string, unknown>;
  return {
    name: clip(record.name, 120),
    id_number: clip(record.id_number, 40),
    tax_id: clip(record.tax_id, 20),
    phone: clip(record.phone, 40),
    email: clip(record.email, 200),
    street: clip(record.street, 160),
    city: clip(record.city, 80),
    postal_code: clip(record.postal_code, 12),
  };
}

function parseInstallment(value: unknown): ContractInstallment {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ContractInputError("Η δόση δεν είναι έγκυρη.");
  const record = value as Record<string, unknown>;
  return { label: clip(record.label, 80), amount: clip(record.amount, 40), due: clip(record.due, 80) };
}

function parseBank(value: unknown): ContractBank {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ContractInputError("Ο λογαριασμός δεν είναι έγκυρος.");
  const record = value as Record<string, unknown>;
  return { bank: clip(record.bank, 80), iban: clip(record.iban, 40), holder: clip(record.holder, 120) };
}

function clip(value: unknown, max: number): string {
  if (value == null) return "";
  if (typeof value !== "string") throw new ContractInputError("Ένα πεδίο του συμφωνητικού δεν είναι κείμενο.");
  const text = value.trim();
  if (text.length > max) throw new ContractInputError("Ένα πεδίο του συμφωνητικού είναι πολύ μεγάλο.");
  return text;
}

function dateOrBlank(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new ContractInputError("Η ημερομηνία του συμφωνητικού δεν είναι έγκυρη.");
  return value;
}

function blankSource(): ContractSource {
  return {
    parties: [],
    contractDate: null,
    depositDate: null,
    eventDate: null,
    eventTypeLabel: null,
    venueName: null,
    guests: null,
    serviceLabel: null,
    menuLabel: null,
    ceremony: null,
    today: "2026-10-21",
  };
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
